import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  IconButton,
  Tooltip,
  CircularProgress,
  Menu,
  MenuItem,
  ListItemText,
  Snackbar,
  Alert,
} from '@mui/material';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import EventIcon from '@mui/icons-material/Event';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import MeetingRoomIcon from '@mui/icons-material/MeetingRoom';
import AddIcon from '@mui/icons-material/Add';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import { Chip } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { format } from 'date-fns';
import { useUserStore } from 'store';
import { can } from '../../utils/access';
import CardHeader from '../shared/CardHeader';
import ScheduleMaintenanceDialog from './ScheduleMaintenanceDialog';
import ConfirmRemoveScheduleDialog from './ConfirmRemoveScheduleDialog';

function SchedulingSection({ maintenance, setMaintenance }) {
  const canSchedule = useUserStore((s) => can(s.currentOrganization, 'work_orders.schedule'));
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [scheduleDialogOpen, setScheduleDialogOpen] = useState(false);
  const [editingSchedule, setEditingSchedule] = useState(null);
  const [confirmRemoveOpen, setConfirmRemoveOpen] = useState(false);
  const [removingSchedule, setRemovingSchedule] = useState(null);
  const [unscheduling, setUnscheduling] = useState(false);
  const [calendarMenuAnchor, setCalendarMenuAnchor] = useState(null);
  const [errorSnackbar, setErrorSnackbar] = useState(null);

  // Read from schedules array with fallback to single schedule
  const schedules = maintenance?.schedules?.length > 0
    ? maintenance.schedules
    : (maintenance?.schedule ? [maintenance.schedule] : []);
  const isScheduled = schedules.length > 0;
  const hasMultiple = schedules.length > 1;
  const isDone = ['completed', 'picked_up', 'cancelled'].includes(maintenance?.state);

  const formatDateWithTime = (isoString) => {
    if (!isoString) return '';
    const date = new Date(isoString);
    return date.toLocaleDateString('fi-FI', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatTime = (isoString) => {
    if (!isoString) return '';
    const date = new Date(isoString);
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    return `${hours}:${minutes}`;
  };

  const isMultiDay = (startIso, endIso) => {
    if (!startIso || !endIso) return false;
    const start = new Date(startIso);
    const end = new Date(endIso);
    return start.toDateString() !== end.toDateString();
  };

  const formatScheduleRange = (startIso, endIso) => {
    if (!startIso || !endIso) return '';
    const start = new Date(startIso);
    const end = new Date(endIso);

    if (isMultiDay(startIso, endIso)) {
      const startStr = start.toLocaleDateString('fi-FI', {
        weekday: 'short',
        day: 'numeric',
        month: 'numeric',
      });
      const endStr = end.toLocaleDateString('fi-FI', {
        weekday: 'short',
        day: 'numeric',
        month: 'numeric',
      });
      return `${startStr} ${formatTime(startIso)} - ${endStr} ${formatTime(endIso)}`;
    } else {
      return `${formatDateWithTime(startIso)} - ${formatTime(endIso)}`;
    }
  };

  const formatDuration = (hours) => {
    if (!hours) return '';
    const totalMinutes = Math.round(hours * 60);
    const h = Math.floor(totalMinutes / 60);
    const m = totalMinutes % 60;
    if (h === 0) return `${m} ${t('min')}`;
    if (m === 0) return `${h} ${t('h')}`;
    return `${h} ${t('h')} ${m} ${t('min')}`;
  };

  // The slot's hours inside the workshop's opening hours (and its mechanics' shifts), with the
  // number of days they fall on when there are several: a job booked Monday 09:00 to Tuesday
  // 16:00 reads "15 t (2 työpäivää)", not the 31 h from start to end. A slot entirely outside
  // the hours has no business hours and shows its raw span instead.
  const formatSlotDuration = (sched) => {
    const hours = sched.business_hours || sched.duration_hours;
    if (!hours) return '';
    const text = formatDuration(hours);
    if (sched.business_days > 1) {
      return `${text} (${t('workingDaysCount', { count: sched.business_days })})`;
    }
    return text;
  };

  const handleViewInCalendar = (schedule) => {
    if (schedule?.start_time) {
      // The local day: toISOString() gives the UTC one, a day early before 03:00 in Finland.
      const dateStr = format(new Date(schedule.start_time), 'yyyy-MM-dd');
      navigate(`/calendar?date=${dateStr}&highlight=${maintenance.id}&view=all`);
    } else {
      navigate('/calendar');
    }
  };

  const handleViewInCalendarClick = (event) => {
    if (hasMultiple) {
      setCalendarMenuAnchor(event.currentTarget);
    } else {
      handleViewInCalendar(schedules[0]);
    }
  };

  const handleReschedule = (schedule) => {
    setEditingSchedule(schedule);
    setScheduleDialogOpen(true);
  };

  const handleAddSchedule = () => {
    setEditingSchedule(null);
    setScheduleDialogOpen(true);
  };

  const handleRemoveSchedule = (schedule) => {
    setRemovingSchedule(schedule);
    setConfirmRemoveOpen(true);
  };

  const handleConfirmRemove = async () => {
    setUnscheduling(true);
    try {
      if (hasMultiple && removingSchedule) {
        // Multiple schedules: delete individual schedule directly
        await axios.delete(`/api/scheduling/schedules/${removingSchedule.id}/`);
        // Refresh maintenance data
        const response = await axios.get(`/api/maintenances/${maintenance.id}/`);
        setMaintenance(response.data);
      } else {
        // Single schedule: use unschedule endpoint
        const response = await axios.delete(`/api/maintenances/${maintenance.id}/unschedule/`);
        setMaintenance(response.data);
      }
      setConfirmRemoveOpen(false);
      setRemovingSchedule(null);
    } catch (error) {
      if (error.response?.status === 409) {
        // Multiple schedules detected — refresh to show multi-schedule UI
        setConfirmRemoveOpen(false);
        setRemovingSchedule(null);
        const response = await axios.get(`/api/maintenances/${maintenance.id}/`);
        setMaintenance(response.data);
        setErrorSnackbar(t('This work order has multiple schedules. Remove them individually.'));
      } else {
        setErrorSnackbar(t('Failed to remove schedule'));
      }
    } finally {
      setUnscheduling(false);
    }
  };

  const handleRemoveAll = async () => {
    setUnscheduling(true);
    try {
      const response = await axios.post(`/api/maintenances/${maintenance.id}/unschedule-all/`);
      setMaintenance(response.data);
      setConfirmRemoveOpen(false);
      setRemovingSchedule(null);
    } catch (error) {
      setErrorSnackbar(t('Failed to remove schedule'));
    } finally {
      setUnscheduling(false);
    }
  };

  // Compact reminder-status chips for one schedule (from the maintenance
  // serializer's per-schedule reminders summary). Cancelled rows are excluded
  // server-side; pending/sent/failed/skipped each get one small chip.
  const renderReminderChips = (sched) => {
    const rows = sched.reminders || [];
    if (rows.length === 0) return null;
    const chipFor = (r) => {
      // Kind-aware labels: a pending/failed confirmation must not read "Muistutus"
      const prefix = r.kind === 'notification' ? 'chip_notification' : 'chip';
      let label;
      let color = 'default';
      if (r.status === 'scheduled') {
        const d = new Date(r.scheduled_for);
        const timeStr = d.toLocaleDateString('fi-FI', {
          day: 'numeric',
          month: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        });
        label = t(`schedule_reminders.${prefix}_pending`, { time: timeStr });
      } else if (r.status === 'sent') {
        label = t(`schedule_reminders.${prefix}_sent`);
        color = 'success';
      } else if (r.status === 'failed') {
        label = t(`schedule_reminders.${prefix}_failed`);
        color = 'error';
      } else {
        label = t(`schedule_reminders.${prefix}_skipped`);
      }
      return (
        <Chip
          key={r.id}
          icon={<NotificationsNoneIcon />}
          label={label}
          size="small"
          color={color}
          variant="outlined"
          sx={{ height: 20, fontSize: '11px' }}
        />
      );
    };
    return (
      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
        {rows.map(chipFor)}
      </Box>
    );
  };

  // Mechanics booked on one slot (the slot's own team). The work order team shown in
  // the mechanics card can be wider than any single slot.
  const slotMechanicNames = (sched) => {
    if (!Array.isArray(sched?.employee_ids)) return null; // older payload, nothing to show
    const byId = new Map((maintenance?.employees || []).map((e) => [e.id, e.name]));
    return sched.employee_ids.map((id) => byId.get(id)).filter(Boolean);
  };

  const renderSlotMechanics = (sched, sx = {}) => {
    const names = slotMechanicNames(sched);
    if (names === null) return null;
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0, ...sx }}>
        <PersonOutlineIcon sx={{ fontSize: 15, color: 'text.disabled', flexShrink: 0 }} />
        <Typography
          data-testid="schedule-slot-mechanics"
          sx={{
            fontSize: '12px',
            color: names.length ? 'text.secondary' : 'text.disabled',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {names.length ? names.join(', ') : t('No mechanic')}
        </Typography>
      </Box>
    );
  };

  // Render a single schedule slot row (used in multi-schedule list)
  const renderScheduleSlot = (sched, index) => (
    <Box
      key={sched.id || index}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        py: 1.5,
        px: 1,
        borderRadius: '8px',
        '&:hover': { bgcolor: 'action.hover' },
        borderBottom: index < schedules.length - 1 ? '1px solid' : 'none',
        borderColor: 'divider',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
        <EventIcon sx={{ fontSize: 18, color: 'secondary.main', flexShrink: 0 }} />
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: '14px', fontWeight: 500 }}>
            {formatScheduleRange(sched.start_time, sched.end_time)}
          </Typography>
          {sched.service_bay_name && (
            <Typography sx={{ fontSize: '12px', color: 'text.secondary' }}>
              {sched.service_bay_name}
            </Typography>
          )}
          {renderSlotMechanics(sched, { mt: 0.25 })}
          {renderReminderChips(sched)}
        </Box>
      </Box>
      {!isDone && canSchedule && (
        <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
          <Tooltip title={t('Reschedule')}>
            <IconButton size="small" onClick={() => handleReschedule(sched)}>
              <EditIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title={t('Remove schedule')}>
            <IconButton size="small" onClick={() => handleRemoveSchedule(sched)} color="error">
              <DeleteIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        </Box>
      )}
    </Box>
  );

  return (
    <>
      <Paper
        elevation={0}
        sx={{
          mt: 3,
          border: '1px solid',
          borderColor: isScheduled ? 'divider' : (isDone ? 'divider' : 'info.main'),
          borderStyle: isScheduled ? 'solid' : 'dashed',
        }}
      >
        <CardHeader
          title={t('Scheduling')}
          icon={<CalendarMonthIcon sx={{ fontSize: 20 }} />}
          action={
            <Box sx={{ display: 'flex', gap: 1 }}>
              {isScheduled && (
                <Tooltip title={t('View in calendar')}>
                  <IconButton
                    size="small"
                    onClick={handleViewInCalendarClick}
                    sx={{ color: 'secondary.main' }}
                  >
                    <OpenInNewIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              {!isDone && canSchedule && isScheduled && !hasMultiple && (
                <Tooltip title={t('Reschedule')}>
                  <IconButton
                    size="small"
                    onClick={() => handleReschedule(schedules[0])}
                    sx={{ color: 'text.secondary' }}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          }
        />

        <Box sx={{ p: 2.5 }}>
          {isScheduled ? (
            <Box>
              {hasMultiple ? (
                // 2+ schedules: list with per-slot actions
                <>
                  {schedules.map((sched, idx) => renderScheduleSlot(sched, idx))}
                  <Box sx={{ display: 'flex', gap: 1.5, mt: 2.5, flexWrap: 'wrap' }}>
                    <Button
                      variant="outlined"
                      startIcon={<OpenInNewIcon />}
                      onClick={handleViewInCalendarClick}
                      sx={{
                        borderRadius: '10px',
                        borderColor: 'divider',
                        color: 'secondary.main',
                        textTransform: 'none',
                        fontWeight: 500,
                        fontFamily: "'Plus Jakarta Sans', sans-serif",
                        '&:hover': {
                          borderColor: 'secondary.main',
                          bgcolor: 'transparent',
                        },
                      }}
                    >
                      {t('View in calendar')}
                    </Button>
                    {!isDone && canSchedule && (
                      <>
                        <Button
                          variant="outlined"
                          color="error"
                          startIcon={<DeleteIcon />}
                          onClick={() => {
                            setRemovingSchedule(null);
                            setConfirmRemoveOpen(true);
                          }}
                          sx={{
                            borderRadius: '10px',
                            textTransform: 'none',
                            fontWeight: 500,
                            fontFamily: "'Plus Jakarta Sans', sans-serif",
                          }}
                        >
                          {t('Remove all schedules')}
                        </Button>
                      </>
                    )}
                  </Box>
                  {!isDone && canSchedule && (
                    <Box sx={{ display: 'flex', gap: 1, mt: 1.5, flexWrap: 'wrap' }}>
                      <Button
                        size="small"
                        startIcon={<CalendarMonthIcon sx={{ fontSize: 16 }} />}
                        onClick={() => navigate(`/calendar?scheduleMaintenanceId=${maintenance.id}`)}
                        sx={{
                          textTransform: 'none',
                          color: 'text.secondary',
                          fontSize: '13px',
                          fontFamily: "'Plus Jakarta Sans', sans-serif",
                          '&:hover': { color: 'secondary.main' },
                        }}
                      >
                        {t('Add time slot in calendar')}
                      </Button>
                      <Button
                        size="small"
                        startIcon={<AddIcon sx={{ fontSize: 16 }} />}
                        onClick={handleAddSchedule}
                        sx={{
                          textTransform: 'none',
                          color: 'text.secondary',
                          fontSize: '13px',
                          fontFamily: "'Plus Jakarta Sans', sans-serif",
                          '&:hover': { color: 'secondary.main' },
                        }}
                      >
                        {t('Add time slot with quick schedule')}
                      </Button>
                    </Box>
                  )}
                </>
              ) : (
                // 1 schedule: identical to previous single-schedule UI
                <>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <Box
                      sx={{
                        width: 48,
                        height: 48,
                        borderRadius: '12px',
                        bgcolor: 'rgba(49, 130, 206, 0.1)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <EventIcon sx={{ fontSize: 24, color: 'secondary.main' }} />
                    </Box>
                    <Box>
                      <Typography
                        sx={{
                          fontSize: '16px',
                          fontWeight: 600,
                          color: 'text.primary',
                          fontFamily: "'Plus Jakarta Sans', sans-serif",
                        }}
                      >
                        {formatScheduleRange(schedules[0].start_time, schedules[0].end_time)}
                      </Typography>
                      {formatSlotDuration(schedules[0]) && (
                        <Typography
                          sx={{ fontSize: '13px', color: 'text.secondary' }}
                          data-testid="schedule-duration"
                        >
                          {formatSlotDuration(schedules[0])}
                        </Typography>
                      )}
                      {renderReminderChips(schedules[0])}
                    </Box>
                  </Box>

                  {schedules[0].service_bay_name && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, ml: 0.5 }}>
                      <MeetingRoomIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
                      <Typography sx={{ fontSize: '13px', color: 'text.secondary' }}>
                        {schedules[0].service_bay_name}
                      </Typography>
                    </Box>
                  )}
                  {renderSlotMechanics(schedules[0], { mb: 2, ml: 0.5 })}

                  <Box sx={{ display: 'flex', gap: 1.5, mt: 2.5 }}>
                    <Button
                      variant="outlined"
                      startIcon={<OpenInNewIcon />}
                      onClick={() => handleViewInCalendar(schedules[0])}
                      sx={{
                        borderRadius: '10px',
                        borderColor: 'divider',
                        color: 'secondary.main',
                        textTransform: 'none',
                        fontWeight: 500,
                        fontFamily: "'Plus Jakarta Sans', sans-serif",
                        '&:hover': {
                          borderColor: 'secondary.main',
                          bgcolor: 'transparent',
                        },
                      }}
                    >
                      {t('View in calendar')}
                    </Button>
                    {!isDone && canSchedule && (
                      <Button
                        variant="outlined"
                        color="error"
                        startIcon={<DeleteIcon />}
                        onClick={() => handleRemoveSchedule(schedules[0])}
                        sx={{
                          borderRadius: '10px',
                          textTransform: 'none',
                          fontWeight: 500,
                          fontFamily: "'Plus Jakarta Sans', sans-serif",
                        }}
                      >
                        {t('Remove schedule')}
                      </Button>
                    )}
                  </Box>
                  {!isDone && canSchedule && (
                    <Box sx={{ display: 'flex', gap: 1, mt: 1.5, flexWrap: 'wrap' }}>
                      <Button
                        size="small"
                        startIcon={<CalendarMonthIcon sx={{ fontSize: 16 }} />}
                        onClick={() => navigate(`/calendar?scheduleMaintenanceId=${maintenance.id}`)}
                        sx={{
                          textTransform: 'none',
                          color: 'text.secondary',
                          fontSize: '13px',
                          fontFamily: "'Plus Jakarta Sans', sans-serif",
                          '&:hover': { color: 'secondary.main' },
                        }}
                      >
                        {t('Add time slot in calendar')}
                      </Button>
                      <Button
                        size="small"
                        startIcon={<AddIcon sx={{ fontSize: 16 }} />}
                        onClick={handleAddSchedule}
                        sx={{
                          textTransform: 'none',
                          color: 'text.secondary',
                          fontSize: '13px',
                          fontFamily: "'Plus Jakarta Sans', sans-serif",
                          '&:hover': { color: 'secondary.main' },
                        }}
                      >
                        {t('Add time slot with quick schedule')}
                      </Button>
                    </Box>
                  )}
                </>
              )}
            </Box>
          ) : (
            // 0 schedules: unchanged from today
            <Box>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  mb: isDone ? 0 : 2,
                }}
              >
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: '12px',
                    bgcolor: isDone ? 'action.hover' : 'rgba(49, 130, 206, 0.1)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <CalendarMonthIcon
                    sx={{ fontSize: 24, color: isDone ? 'text.disabled' : 'info.main' }}
                  />
                </Box>
                <Box>
                  <Typography
                    sx={{
                      fontSize: '15px',
                      fontWeight: 500,
                      color: isDone ? 'text.disabled' : 'info.main',
                      fontFamily: "'Plus Jakarta Sans', sans-serif",
                    }}
                  >
                    {t('Not scheduled')}
                  </Typography>
                  {!isDone && canSchedule && (
                    <Typography sx={{ fontSize: '13px', color: 'text.secondary' }}>
                      {t('Schedule this maintenance using the buttons below')}
                    </Typography>
                  )}
                </Box>
              </Box>

              {!isDone && canSchedule && (
                <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                  <Button
                    variant="contained"
                    startIcon={<OpenInNewIcon />}
                    onClick={() => navigate(`/calendar?scheduleMaintenanceId=${maintenance.id}`)}
                    sx={{
                      bgcolor: 'secondary.main',
                      borderRadius: '10px',
                      textTransform: 'none',
                      fontWeight: 500,
                      fontFamily: "'Plus Jakarta Sans', sans-serif",
                      '&:hover': {
                        bgcolor: 'secondary.dark',
                      },
                    }}
                  >
                    {t('Schedule in calendar')}
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<EventIcon />}
                    onClick={handleAddSchedule}
                    sx={{
                      borderRadius: '10px',
                      borderColor: 'divider',
                      color: 'text.secondary',
                      textTransform: 'none',
                      fontWeight: 500,
                      fontFamily: "'Plus Jakarta Sans', sans-serif",
                      '&:hover': {
                        borderColor: 'text.secondary',
                        bgcolor: 'transparent',
                      },
                    }}
                  >
                    {t('Quick schedule')}
                  </Button>
                </Box>
              )}
            </Box>
          )}
        </Box>
      </Paper>

      {/* Calendar slot picker menu (for multiple schedules) */}
      <Menu
        anchorEl={calendarMenuAnchor}
        open={Boolean(calendarMenuAnchor)}
        onClose={() => setCalendarMenuAnchor(null)}
      >
        {schedules.map((sched, idx) => (
          <MenuItem
            key={sched.id || idx}
            onClick={() => {
              setCalendarMenuAnchor(null);
              handleViewInCalendar(sched);
            }}
          >
            <ListItemText primary={formatScheduleRange(sched.start_time, sched.end_time)} />
          </MenuItem>
        ))}
      </Menu>

      <ScheduleMaintenanceDialog
        open={scheduleDialogOpen}
        handleClose={() => {
          setScheduleDialogOpen(false);
          setEditingSchedule(null);
        }}
        maintenance={maintenance}
        setMaintenance={setMaintenance}
        existingSchedule={editingSchedule}
      />

      <ConfirmRemoveScheduleDialog
        open={confirmRemoveOpen}
        handleClose={() => {
          setConfirmRemoveOpen(false);
          setRemovingSchedule(null);
        }}
        onConfirm={handleConfirmRemove}
        onRemoveAll={hasMultiple && !removingSchedule ? handleRemoveAll : null}
        loading={unscheduling}
        hasActiveBooking={maintenance?.has_active_booking}
        schedule={removingSchedule}
        hasMultiple={hasMultiple}
      />

      <Snackbar
        open={!!errorSnackbar}
        autoHideDuration={5000}
        onClose={() => setErrorSnackbar(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setErrorSnackbar(null)} severity="error" variant="filled">
          {errorSnackbar}
        </Alert>
      </Snackbar>
    </>
  );
}

export default SchedulingSection;
